/**
 * @fileoverview Token Storage Abstraction
 * @task B.1.4.2 - Abstract token storage for Auth0 preparation
 *
 * This module provides an abstraction layer for JWT token storage that supports
 * multiple storage strategies:
 *
 * 1. **LocalStorageTokenStorage**: Stores tokens in localStorage (current behavior).
 *    - Used in mock auth mode for local development
 *    - Tokens are accessible via JavaScript (XSS vulnerable in production)
 *    - Suitable for development and testing only
 *
 * 2. **CookieTokenStorage**: Placeholder for httpOnly cookie-based storage.
 *    - Used when Auth0 is integrated (VITE_USE_MOCK_AUTH=false)
 *    - Tokens are managed by the backend via httpOnly cookies
 *    - The frontend cannot read/write tokens directly
 *    - API client uses `credentials: 'include'` to send cookies automatically
 *
 * **Design Rationale:**
 * - Abstract storage mechanism to prepare for Auth0 httpOnly cookie migration
 * - Maintain backward compatibility with existing mock auth implementation
 * - Single point of change when migrating to production auth
 *
 * **Security Note:**
 * When Auth0 is integrated, the backend will set httpOnly cookies on login.
 * The frontend won't need to handle tokens directly - they're sent automatically
 * with each request via the cookie header.
 */

// Storage keys for localStorage
const ACCESS_TOKEN_KEY = 'finova_access_token';
const REFRESH_TOKEN_KEY = 'finova_refresh_token';
const AUTH_STATE_KEY = 'finova_auth';

/**
 * Token data structure for storage operations.
 *
 * @property accessToken - JWT access token for API authentication
 * @property refreshToken - Token used to obtain new access tokens (optional)
 */
export interface TokenData {
  accessToken: string | null;
  refreshToken?: string | null;
}

/**
 * Token storage interface defining the contract for all storage implementations.
 *
 * **Usage:**
 * Components should depend on this interface, not concrete implementations.
 * This allows easy switching between storage strategies via dependency injection.
 *
 * @example
 * ```typescript
 * // Get the appropriate storage based on environment
 * const storage = getTokenStorage();
 *
 * // Store tokens after login
 * storage.setTokens({ accessToken: 'jwt...', refreshToken: 'refresh...' });
 *
 * // Retrieve access token for API calls
 * const token = storage.getAccessToken();
 *
 * // Clear tokens on logout
 * storage.clearTokens();
 * ```
 */
export interface TokenStorage {
  /**
   * Retrieve the current access token.
   *
   * @returns The access token string if available, null otherwise.
   *          For cookie-based storage, this always returns null as tokens
   *          are managed by the browser and sent automatically.
   */
  getAccessToken(): string | null;

  /**
   * Retrieve the current refresh token.
   *
   * @returns The refresh token string if available, null otherwise.
   *          For cookie-based storage, this always returns null.
   */
  getRefreshToken(): string | null;

  /**
   * Store access and optionally refresh tokens.
   *
   * @param tokens - Token data to store
   *
   * **Note:** For cookie-based storage, this is a no-op as the backend
   * manages token storage via Set-Cookie headers.
   */
  setTokens(tokens: TokenData): void;

  /**
   * Clear all stored tokens.
   *
   * **Note:** For cookie-based storage, the actual cookies are cleared
   * by the backend on logout. This method clears any local state.
   */
  clearTokens(): void;

  /**
   * Check if tokens are currently stored/available.
   *
   * @returns true if an access token is available, false otherwise.
   *          For cookie-based storage, this checks if the user appears
   *          to be authenticated (based on local state, not cookie presence).
   */
  hasTokens(): boolean;

  /**
   * Get the storage type identifier for debugging.
   *
   * @returns A string identifying the storage type ('localStorage' | 'cookie')
   */
  getStorageType(): 'localStorage' | 'cookie';
}

/**
 * LocalStorage-based token storage implementation.
 *
 * **Use Case:** Development/mock authentication mode (VITE_USE_MOCK_AUTH=true)
 *
 * **Security Warning:** This implementation stores tokens in localStorage which
 * is accessible via JavaScript and vulnerable to XSS attacks. It should only
 * be used for local development and testing, never in production.
 *
 * **Storage Structure:**
 * - finova_access_token: JWT access token
 * - finova_refresh_token: Refresh token for token renewal
 * - finova_auth: Legacy key for backward compatibility with existing mock auth
 *
 * @implements {TokenStorage}
 */
export class LocalStorageTokenStorage implements TokenStorage {
  /**
   * Retrieve access token from localStorage.
   *
   * First checks for the dedicated access token key, then falls back to
   * the legacy auth state object for backward compatibility.
   *
   * @returns The access token or null if not found
   */
  getAccessToken(): string | null {
    try {
      // Try new key first
      const token = localStorage.getItem(ACCESS_TOKEN_KEY);
      if (token) {
        return token;
      }

      // Fall back to legacy storage format for backward compatibility
      const authState = localStorage.getItem(AUTH_STATE_KEY);
      if (authState) {
        const parsed = JSON.parse(authState) as { token?: string };
        return parsed.token || null;
      }

      return null;
    } catch (error) {
      console.error('[TokenStorage] Failed to get access token:', error);
      return null;
    }
  }

  /**
   * Retrieve refresh token from localStorage.
   *
   * @returns The refresh token or null if not found
   */
  getRefreshToken(): string | null {
    try {
      return localStorage.getItem(REFRESH_TOKEN_KEY);
    } catch (error) {
      console.error('[TokenStorage] Failed to get refresh token:', error);
      return null;
    }
  }

  /**
   * Store tokens in localStorage.
   *
   * Also updates the legacy auth state for backward compatibility with
   * the existing mock auth implementation.
   *
   * @param tokens - Token data to store
   */
  setTokens(tokens: TokenData): void {
    try {
      if (tokens.accessToken) {
        localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
      } else {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
      }

      if (tokens.refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
      } else {
        localStorage.removeItem(REFRESH_TOKEN_KEY);
      }
    } catch (error) {
      console.error('[TokenStorage] Failed to set tokens:', error);
    }
  }

  /**
   * Clear all tokens from localStorage.
   *
   * Removes both new token keys and the legacy auth state.
   */
  clearTokens(): void {
    try {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(AUTH_STATE_KEY);
    } catch (error) {
      console.error('[TokenStorage] Failed to clear tokens:', error);
    }
  }

  /**
   * Check if an access token is stored.
   *
   * @returns true if an access token exists in localStorage
   */
  hasTokens(): boolean {
    return this.getAccessToken() !== null;
  }

  /**
   * Get storage type identifier.
   *
   * @returns 'localStorage'
   */
  getStorageType(): 'localStorage' | 'cookie' {
    return 'localStorage';
  }
}

/**
 * Cookie-based token storage implementation (placeholder for Auth0 integration).
 *
 * **Use Case:** Production authentication with Auth0 (VITE_USE_MOCK_AUTH=false)
 *
 * **How It Works:**
 * When Auth0 is integrated, authentication flow changes:
 *
 * 1. User logs in via Auth0 Universal Login
 * 2. Auth0 redirects back with an authorization code
 * 3. Backend exchanges code for tokens and sets httpOnly cookies
 * 4. All subsequent API requests include cookies automatically
 * 5. Frontend never directly handles JWT tokens
 *
 * **Security Benefits:**
 * - httpOnly cookies are not accessible via JavaScript (XSS protection)
 * - Secure flag ensures cookies only sent over HTTPS
 * - SameSite attribute prevents CSRF attacks
 * - Tokens are automatically managed by the browser
 *
 * **Implementation Note:**
 * Most methods return null or are no-ops because the frontend doesn't manage
 * tokens directly. The actual token handling is done by:
 * - Backend: Sets/clears httpOnly cookies
 * - Browser: Automatically includes cookies in requests
 * - API Client: Uses `credentials: 'include'` for cookie transmission
 *
 * @implements {TokenStorage}
 */
export class CookieTokenStorage implements TokenStorage {
  /**
   * Access token is managed by httpOnly cookie - not readable by JavaScript.
   *
   * The Auth0/backend flow manages the token via httpOnly cookies.
   * The API client includes credentials automatically with each request.
   *
   * @returns null - Token is not accessible to JavaScript
   */
  getAccessToken(): string | null {
    // httpOnly cookies are not accessible via JavaScript
    // The browser automatically sends them with requests
    return null;
  }

  /**
   * Refresh token is managed by httpOnly cookie - not readable by JavaScript.
   *
   * The backend handles token refresh automatically when cookies are sent.
   *
   * @returns null - Token is not accessible to JavaScript
   */
  getRefreshToken(): string | null {
    // httpOnly cookies are not accessible via JavaScript
    return null;
  }

  /**
   * Token storage is handled by the backend via Set-Cookie headers.
   *
   * This is a no-op because:
   * 1. Backend sets httpOnly cookies after successful authentication
   * 2. Frontend cannot and should not write to httpOnly cookies
   * 3. Attempting to store tokens client-side would bypass security
   *
   * @param _tokens - Ignored; tokens are set by backend
   */
  setTokens(_tokens: TokenData): void {
    // Tokens are set by the backend via Set-Cookie headers
    // Frontend does not manage token storage in cookie mode
    console.debug('[TokenStorage] Cookie mode: tokens managed by backend');
  }

  /**
   * Clear tokens by calling the logout endpoint.
   *
   * The actual cookie clearing is done by the backend's logout endpoint
   * which sets expired cookies. This method just clears any local state.
   *
   * **Note:** The backend logout call should be made separately by the
   * auth service before calling this method.
   */
  clearTokens(): void {
    // Cookies are cleared by the backend via Set-Cookie with expired date
    // This is called after the logout API request completes
    console.debug('[TokenStorage] Cookie mode: tokens cleared by backend');
  }

  /**
   * Check if user appears to be authenticated.
   *
   * Since we can't read httpOnly cookies, we rely on:
   * 1. Auth0 SDK's isAuthenticated state
   * 2. Successful API responses (valid session)
   *
   * This method returns false as a conservative default.
   * The actual authentication state is determined by the Auth0 SDK.
   *
   * @returns false - Cannot determine token presence from httpOnly cookies
   */
  hasTokens(): boolean {
    // Cannot check httpOnly cookies from JavaScript
    // Auth state is determined by Auth0 SDK or by making an API call
    return false;
  }

  /**
   * Get storage type identifier.
   *
   * @returns 'cookie'
   */
  getStorageType(): 'localStorage' | 'cookie' {
    return 'cookie';
  }
}

// Singleton instances to avoid repeated instantiation
let localStorageInstance: LocalStorageTokenStorage | null = null;
let cookieStorageInstance: CookieTokenStorage | null = null;

/**
 * Factory function to get the appropriate token storage implementation.
 *
 * Selection is based on the VITE_USE_MOCK_AUTH environment variable:
 * - 'true': Returns LocalStorageTokenStorage for mock auth mode
 * - 'false' or undefined: Returns CookieTokenStorage for production/Auth0
 *
 * **Usage:**
 * ```typescript
 * import { getTokenStorage } from '@/services/auth/token-storage';
 *
 * const storage = getTokenStorage();
 * const token = storage.getAccessToken();
 * ```
 *
 * **Singleton Pattern:**
 * Returns the same instance for each storage type to ensure consistent
 * state across the application.
 *
 * @returns The appropriate TokenStorage implementation based on environment
 */
export function getTokenStorage(): TokenStorage {
  const useMockAuth = import.meta.env.VITE_USE_MOCK_AUTH === 'true';

  if (useMockAuth) {
    if (!localStorageInstance) {
      localStorageInstance = new LocalStorageTokenStorage();
    }
    return localStorageInstance;
  }

  if (!cookieStorageInstance) {
    cookieStorageInstance = new CookieTokenStorage();
  }
  return cookieStorageInstance;
}

/**
 * Check if the application is running in mock authentication mode.
 *
 * **Use Case:** Conditional rendering or behavior based on auth mode.
 *
 * @returns true if VITE_USE_MOCK_AUTH is 'true', false otherwise
 *
 * @example
 * ```typescript
 * if (isMockAuthMode()) {
 *   // Show mock login form
 * } else {
 *   // Redirect to Auth0
 * }
 * ```
 */
export function isMockAuthMode(): boolean {
  return import.meta.env.VITE_USE_MOCK_AUTH === 'true';
}

/**
 * Reset storage instances (for testing purposes only).
 *
 * This function clears the singleton instances, allowing tests to start
 * with a fresh state. Should not be used in production code.
 *
 * @internal
 */
export function _resetStorageInstances(): void {
  localStorageInstance = null;
  cookieStorageInstance = null;
}
